import { useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { AddAllowancePayment } from "../../api/apirequest";

const AllowancePaymentModal = ({ fetchOrders, isOpen, onClose, users }) => {
  const [formData, setFormData] = useState({
    userID: 0,
    paymentDate: new Date().toISOString().slice(0, 10), // Changed to match date input
    amount: "",
    comment: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Updated validation to match actual form fields
  const validateFormData = (data) => {
    if (!data.userID) return "User selection is required.";
    if (!data.paymentDate) return "Payment date is required.";
    if (!data.amount || data.amount <= 0) return "A valid amount is required.";
    if (!data.comment.trim()) return "Comment is required.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const validationError = validateFormData(formData);
    if (validationError) {
      toast.error(validationError);
      setLoading(false);
      return;
    }

    try {
      const result = await AddAllowancePayment({ ...formData });

      if (result && result.success) {
        toast.success(result.message || "Allowance payment added successfully!");
        fetchOrders();
        onClose();
      } else {
        toast.error(result.message || "Failed to add allowance payment.");
      }
    } catch (error) {
      console.error("Error during submission:", error);
      toast.error("An unexpected error occurred.");
    } finally {
      setLoading(false);
      // Reset form
      setFormData({
        userID: 0,
        paymentDate: new Date().toISOString().slice(0, 10),
        amount: "",
        comment: "",
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (selectedOption) => {
    setFormData({
      ...formData,
      userID: selectedOption ? selectedOption.value : 0,
    });
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Add Allowance Payment</h2>
          <button style={styles.closeButton} onClick={onClose} disabled={loading}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>User</label>
            <Select
              options={users.map((user) => ({
                value: user.id,
                label: user.name,
              }))}
              onChange={handleSelectChange}
              placeholder="Select User"
              styles={customSelectStyles}
              isClearable
              isDisabled={loading}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Payment Date</label>
            <input
              type="date"
              name="paymentDate"
              value={formData.paymentDate}
              onChange={handleChange}
              required
              style={styles.input}
              disabled={loading}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Amount</label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              required
              style={styles.input}
              placeholder="Enter amount"
              min="0"
              step="0.01"
              disabled={loading}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Comment</label>
            <textarea
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              required
              style={styles.textarea}
              placeholder="Add a comment"
              disabled={loading}
            />
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.buttonContainer}>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={styles.cancelButton}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#ffffff",
    padding: "24px",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "400px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    position: "relative",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#1a2a3a",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    color: "#7fa7ba",
    cursor: "pointer",
    padding: "0",
    transition: "color 0.2s ease",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#2c3d4e",
  },
  input: {
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #d1dbe3",
    borderRadius: "6px",
    outline: "none",
    transition: "border-color 0.2s ease",
    width: "100%",
    boxSizing: "border-box",
  },
  textarea: {
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #d1dbe3",
    borderRadius: "6px",
    outline: "none",
    minHeight: "80px",
    resize: "vertical",
    transition: "border-color 0.2s ease",
    width: "100%",
    boxSizing: "border-box",
  },
  error: {
    color: "#e53e3e",
    fontSize: "12px",
    textAlign: "center",
  },
  buttonContainer: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
  },
  submitButton: {
    backgroundColor: "#44dc94",
    color: "#ffffff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.2s ease, transform 0.1s ease",
    flex: "1",
  },
  cancelButton: {
    backgroundColor: "#f5f7fa",
    color: "#7fa7ba",
    padding: "10px 20px",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.2s ease, color 0.2s ease",
    flex: "1",
  },
};

// Custom styles for react-select without green backgrounds
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    padding: "4px",
    borderRadius: "6px",
    borderColor: state.isFocused ? "#7fa7ba" : "#d1dbe3",
    backgroundColor: "#ffffff",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(127, 167, 186, 0.2)" : "none",
    "&:hover": {
      borderColor: "#7fa7ba",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    backgroundColor: "#ffffff",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#f5f7fa"
      : state.isFocused
      ? "#ebedf0"
      : "#ffffff",
    color: state.isSelected ? "#2c3d4e" : "#2c3d4e",
    padding: "8px 12px",
    "&:active": {
      backgroundColor: "#d1dbe3",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#7fa7ba",
    fontSize: "14px",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#2c3d4e",
    fontSize: "14px",
  }),
};

export default AllowancePaymentModal;